import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconDeviceFloppy, IconPencil, IconX } from "@tabler/icons-react";
import Konva from "konva";
import { useChartDocument } from "./hooks/useChartDocument";
import { useDrawingTool } from "./hooks/useDrawingTool";
import ChartCanvas, {
  ChartCanvasRef,
  type TextInputState,
} from "./ChartCanvas";
import ChartToolbar from "./ChartToolbar";
import { downloadChart } from "./utils/chartExport";
import {
  ChartDocument,
  DrawingTool,
  Stroke,
  ToolSettings,
} from "./types/chart";
import { DocumentVersion } from "../../../types/salonDocument";
import {
  formatSalonDocumentVersionTimestamp,
  initialsFromStaffDisplayName,
  salonDocumentVersionStaffDisplayName,
} from "../../../utils/salonDocumentVersionUi";
import { CHART_DISPLAY_NAME_MAX_CHARS } from "./constants/chartUploadLimits";
import primaryAction from "../../common/primaryFilledButton.module.css";
import styles from "./ChartEditor.module.css";

interface ChartEditorProps {
  baseImageUrl: string;
  diagramName: string;
  /** Shown under the title when set (e.g. full client name) */
  clientDisplayName?: string;
  onBack: () => void;
  onSave?: (document: ChartDocument, stage: Konva.Stage) => Promise<void>;
  initialDocument?: ChartDocument;
  /**
   * Callback to fetch version history for this chart.
   * `currentVersionId` (when present) is the backend's authoritative pointer
   * to the active version — necessary because revert can change the active
   * version without bumping versionNumber.
   */
  onLoadVersions?: () => Promise<{
    versions: DocumentVersion[];
    currentVersionId?: number;
  }>;
  /** Callback to load a specific version by URL */
  onLoadVersion?: (version: DocumentVersion) => Promise<ChartDocument | null>;
}

const TOOL_CURSORS: Record<DrawingTool, string> = {
  select: "default",
  pen: "crosshair",
  highlighter: "crosshair",
  eraser: "pointer",
  rectangle: "crosshair",
  circle: "crosshair",
  arrow: "crosshair",
  text: "text",
  emoji: "crosshair",
};

const TEXT_FONT_SIZES: Record<number, number> = {
  2: 14,
  4: 20,
  8: 28,
};

export default function ChartEditor({
  baseImageUrl,
  diagramName,
  clientDisplayName,
  onBack,
  onSave,
  initialDocument,
  onLoadVersions,
  onLoadVersion,
}: ChartEditorProps) {
  const canvasRef = useRef<ChartCanvasRef>(null);
  const {
    document,
    isDirty,
    addStroke,
    removeStroke,
    updateStroke,
    undo,
    redo,
    canUndo,
    canRedo,
    revertStrokesToBaseline,
    clearAllStrokesPermanently,
    hasSessionStrokeEdits,
    loadDocument,
    setBaseImage,
    markClean,
    updateDocumentMetadata,
  } = useChartDocument();

  const [toolSettings, setToolSettings] = useState<ToolSettings>({
    tool: "pen",
    color: "#000000",
    width: 2,
    emoji: "\u{1F600}",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [textInput, setTextInput] = useState<TextInputState | null>(null);
  const [imageLoadKey, setImageLoadKey] = useState(0);
  const initialTitle = initialDocument?.metadata?.title?.trim() || diagramName;
  const [chartTitle, setChartTitle] = useState(() => initialTitle);
  const [baselineTitle, setBaselineTitle] = useState(() => initialTitle);
  const [titleEditing, setTitleEditing] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const lastExternalMetaTitleRef = useRef<string | undefined>(initialTitle);
  const [showRevertSessionConfirm, setShowRevertSessionConfirm] =
    useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [selectedStrokeId, setSelectedStrokeId] = useState<string | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<string | null>(null);

  /**
   * Parent passes `baseImageUrl` (e.g. blob). After restoring a version, `loadDocument`
   * updates `document.baseImageRef` to that version's URL — the canvas must follow it.
   */
  const canvasBaseImageUrl = useMemo(
    () => document.baseImageRef?.trim() || baseImageUrl,
    [document.baseImageRef, baseImageUrl],
  );

  const refreshVersions = useCallback(async () => {
    if (!onLoadVersions) return;
    setLoadingVersions(true);
    try {
      const result = await onLoadVersions();
      setVersions((prev) => {
        // Preserve any optimistic "Saving…" placeholder rows pushed
        // by handleSave whose matching real version hasn't surfaced
        // server-side yet. Without this, a concurrent refresh — say
        // the initial mount load racing the save's refresh, or any
        // re-mount-driven refresh in between — would blow the
        // placeholder away mid-save and the sidebar would flicker
        // instead of staying visibly optimistic. The placeholder is
        // dropped naturally once its versionNumber appears in the
        // server result.
        const realNumbers = new Set(
          result.versions.map((v) => v.versionNumber),
        );
        const pendingPlaceholders = prev.filter(
          (v) =>
            v.versionUuid.startsWith("__saving_placeholder_") &&
            !realNumbers.has(v.versionNumber),
        );
        return [...pendingPlaceholders, ...result.versions];
      });
      setCurrentVersionId(result.currentVersionId ?? null);
    } catch (err) {
      console.error("Failed to load versions:", err);
      setVersions((prev) =>
        prev.filter((v) => v.versionUuid.startsWith("__saving_placeholder_")),
      );
      setCurrentVersionId(null);
    } finally {
      setLoadingVersions(false);
    }
  }, [onLoadVersions]);

  useEffect(() => {
    if (!onLoadVersions) {
      setVersions([]);
      setCurrentVersionId(null);
      return;
    }
    void refreshVersions();
  }, [onLoadVersions, refreshVersions]);

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
    [versions],
  );

  /**
   * Prefer the API's `currentVersionId` pointer (which tracks reverts) over
   * the highest-numbered version, since revert does not necessarily bump the
   * version number.
   */
  const currentVersionUuid = useMemo(() => {
    if (sortedVersions.length === 0) return null;
    if (currentVersionId != null) {
      const match = sortedVersions.find(
        (v) => v.versionNumber === currentVersionId,
      );
      if (match) return match.versionUuid;
    }
    return sortedVersions[0].versionUuid;
  }, [sortedVersions, currentVersionId]);

  const didApplyInitial = useRef(false);
  const titleSeedAppliedRef = useRef(false);
  // Load initial document if provided (editing a saved chart); run once per mount
  useEffect(() => {
    if (didApplyInitial.current) return;
    didApplyInitial.current = true;
    if (initialDocument) {
      loadDocument(initialDocument);
    } else {
      setBaseImage(baseImageUrl, "preset");
    }
  }, [initialDocument, baseImageUrl, loadDocument, setBaseImage]);

  /**
   * New charts: copy default title into `document.metadata.title` once so the
   * saved `chartDocument` JSON matches the header field (what the list reads).
   */
  useEffect(() => {
    if (initialDocument || titleSeedAppliedRef.current) return;
    if (!document.baseImageRef) return;
    if (document.metadata?.title?.trim()) {
      titleSeedAppliedRef.current = true;
      return;
    }
    updateDocumentMetadata({ title: diagramName }, { markDirty: false });
    titleSeedAppliedRef.current = true;
  }, [
    initialDocument,
    diagramName,
    document.baseImageRef,
    document.metadata?.title,
    updateDocumentMetadata,
  ]);

  /** After restore / external metadata change, sync title + save baseline (not while user is editing). */
  useEffect(() => {
    const fromDoc = document.metadata?.title?.trim();
    if (typeof fromDoc !== "string") return;
    if (titleEditing) return;
    if (lastExternalMetaTitleRef.current === fromDoc) return;
    lastExternalMetaTitleRef.current = fromDoc;
    setChartTitle(fromDoc);
    setBaselineTitle(fromDoc);
  }, [document.metadata?.title, titleEditing]);

  useEffect(() => {
    if (!titleEditing) return;
    queueMicrotask(() => titleInputRef.current?.focus());
  }, [titleEditing]);

  const commitTitleEdit = useCallback(() => {
    const trimmed = chartTitle.trim();
    setChartTitle(trimmed);
    lastExternalMetaTitleRef.current = trimmed;
    updateDocumentMetadata({ title: trimmed }, { markDirty: false });
    setTitleEditing(false);
  }, [chartTitle, updateDocumentMetadata]);

  const titleDirty = chartTitle.trim() !== baselineTitle.trim();
  const hasUnsavedChanges = isDirty || titleDirty;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  /**
   * After a single-shot placement (text, emoji), drop back to the Select tool
   * so the next tap on canvas selects/moves the just-placed stroke instead of
   * dropping a duplicate / opening an empty text input on top of it. Without
   * this, tablet users perceive the just-saved text as having "disappeared" —
   * the stroke is rendered, but the next tap creates a new empty <input> at
   * the tap position, hiding the saved text behind it.
   */
  const resetToolToSelect = useCallback(() => {
    setToolSettings((prev) =>
      prev.tool === "select" ? prev : { ...prev, tool: "select" },
    );
  }, []);

  const handleStrokeComplete = useCallback(
    (stroke: (typeof document.strokes)[0]) => {
      addStroke(stroke);
      if (stroke.tool === "emoji") {
        resetToolToSelect();
      }
    },
    [addStroke, resetToolToSelect],
  );

  const handleEraseStroke = useCallback(
    (strokeId: string) => {
      removeStroke(strokeId);
    },
    [removeStroke],
  );

  const handleSelectStroke = useCallback((strokeId: string) => {
    setSelectedStrokeId(strokeId);
  }, []);

  const handleDeselectStroke = useCallback(() => {
    setSelectedStrokeId(null);
  }, []);

  const handleStrokeTransform = useCallback(
    (strokeId: string, updates: Partial<Stroke>) => {
      updateStroke(strokeId, updates);
    },
    [updateStroke],
  );

  const handleTextInputRequest = useCallback(
    (pos: { x: number; y: number }) => {
      // CRITICAL: if a text input is already open with typed content, commit
      // it FIRST. React unmounts the old <input> without firing onBlur when
      // we set a new textInput state, so the typed text would otherwise be
      // silently lost. flushPendingText returns true when it committed; in
      // that case we don't open a new input — the commit triggers a tool
      // reset to Select (single-shot UX), and the user can tap Text again
      // to start another box if needed.
      const flushed = canvasRef.current?.flushPendingText() ?? false;
      if (flushed) return;
      const fontSize = TEXT_FONT_SIZES[toolSettings.width] || 20;
      setTextInput({
        id: `ti-${Date.now()}`,
        x: pos.x,
        y: pos.y,
        color: toolSettings.color,
        fontSize,
      });
    },
    [toolSettings.color, toolSettings.width],
  );

  const handleTextSubmit = useCallback(
    (text: string, placement: TextInputState) => {
      const stroke: Stroke = {
        id: `stroke-${Date.now()}-text`,
        tool: "text",
        points: [],
        color: placement.color,
        width: 0,
        opacity: 1,
        x: placement.x,
        y: placement.y,
        text,
        fontSize: placement.fontSize,
      };
      addStroke(stroke);
      setTextInput(null);
      resetToolToSelect();
    },
    [addStroke, resetToolToSelect],
  );

  const handleTextCancel = useCallback(() => {
    setTextInput(null);
    resetToolToSelect();
  }, [resetToolToSelect]);

  const handleRetryImage = useCallback(() => {
    setImageLoadKey((k) => k + 1);
  }, []);

  const handleDownload = useCallback(() => {
    const stage = canvasRef.current?.getStage();
    if (stage) {
      downloadChart(
        stage,
        `${diagramName.replace(/\s+/g, "-").toLowerCase()}-markup.png`,
      );
    }
  }, [diagramName]);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    if (!hasUnsavedChanges) return;
    const stage = canvasRef.current?.getStage();
    if (!stage) return;

    setIsSaving(true);

    // Optimistic placeholder at the top of the version sidebar so the
    // user gets immediate visual feedback that the save is in flight.
    // Replaced with the real new version when refreshVersions runs
    // after onSave resolves; dropped on error.
    const PLACEHOLDER_UUID = `__saving_placeholder_${Date.now()}`;
    let placeholderPushed = false;
    if (onLoadVersions) {
      placeholderPushed = true;
      setVersions((prev) => [
        {
          versionUuid: PLACEHOLDER_UUID,
          versionNumber: (prev[0]?.versionNumber ?? 0) + 1,
          url: "",
          contentType: "image/png",
          fileSize: 0,
          createdDt: new Date().toISOString(),
          displayName: "Saving…",
        } as DocumentVersion,
        ...prev,
      ]);
    }

    try {
      const docToSave: ChartDocument = {
        ...document,
        baseImageRef: canvasBaseImageUrl,
        baseImageSource: canvasBaseImageUrl.startsWith("data:")
          ? "preset"
          : document.baseImageSource === "url"
            ? "url"
            : "upload",
        metadata: {
          ...document.metadata,
          title:
            chartTitle.trim() ||
            document.metadata?.title?.trim() ||
            diagramName,
          updatedAt: new Date().toISOString(),
          createdAt: document.metadata?.createdAt || new Date().toISOString(),
        },
      };
      try {
        await onSave(docToSave, stage);
        markClean();
        setBaselineTitle(chartTitle.trim());
        // Refresh so the placeholder is replaced by the real new
        // version row from the backend. Without this the sidebar
        // shows "Saving…" forever (or the placeholder gets stranded
        // until something else triggers the load).
        if (onLoadVersions) {
          await refreshVersions();
        }
      } catch (err) {
        // Parent handler already shows a user-facing error toast.
        // Swallow the error here so the editor stays open with its dirty
        // state intact, letting the user retry without losing work.
        console.error("Chart save failed; keeping editor open:", err);
        // Drop the placeholder so the user isn't misled into thinking
        // the save succeeded.
        if (placeholderPushed) {
          setVersions((prev) =>
            prev.filter((v) => v.versionUuid !== PLACEHOLDER_UUID),
          );
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    onSave,
    onLoadVersions,
    refreshVersions,
    document,
    canvasBaseImageUrl,
    chartTitle,
    markClean,
    hasUnsavedChanges,
    diagramName,
  ]);

  const handleRevertSessionRequest = useCallback(() => {
    if (hasSessionStrokeEdits) {
      setShowRevertSessionConfirm(true);
    }
  }, [hasSessionStrokeEdits]);

  const handleRevertSessionConfirm = useCallback(() => {
    revertStrokesToBaseline();
    setShowRevertSessionConfirm(false);
    setSelectedStrokeId(null);
  }, [revertStrokesToBaseline]);

  const handleDeleteAllRequest = useCallback(() => {
    if (document.strokes.length > 0) {
      setShowDeleteAllConfirm(true);
    }
  }, [document.strokes.length]);

  const handleDeleteAllConfirm = useCallback(() => {
    clearAllStrokesPermanently();
    setShowDeleteAllConfirm(false);
    setSelectedStrokeId(null);
  }, [clearAllStrokesPermanently]);

  const handleRestoreVersion = useCallback(
    async (version: DocumentVersion) => {
      if (!onLoadVersion) return;
      setRestoringVersion(version.versionUuid);
      try {
        const doc = await onLoadVersion(version);
        if (doc) {
          loadDocument(doc);
          await refreshVersions();
        }
      } catch (err) {
        console.error("Failed to restore version:", err);
      } finally {
        setRestoringVersion(null);
      }
    },
    [onLoadVersion, loadDocument, refreshVersions],
  );

  const handleBackRequest = useCallback(() => {
    if (hasUnsavedChanges && onSave) {
      setShowBackConfirm(true);
    } else {
      onBack();
    }
  }, [hasUnsavedChanges, onSave, onBack]);

  const { currentStroke, handleMouseDown, handleMouseMove, handleMouseUp } =
    useDrawingTool({
      toolSettings,
      onStrokeComplete: handleStrokeComplete,
      onEraseStroke: handleEraseStroke,
      onTextInputRequest: handleTextInputRequest,
      onSelectStroke: handleSelectStroke,
      onDeselectStroke: handleDeselectStroke,
    });

  // Keep a ref to handleSave so the keyboard shortcut effect doesn't
  // need it in deps (avoids stale closure issues)
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;

  const selectedStrokeIdRef = useRef(selectedStrokeId);
  selectedStrokeIdRef.current = selectedStrokeId;

  // Keyboard shortcuts
  useEffect(() => {
    const TOOL_KEYS: Record<string, DrawingTool> = {
      "0": "select",
      "1": "pen",
      "2": "highlighter",
      "3": "eraser",
      "4": "rectangle",
      "5": "circle",
      "6": "arrow",
      "7": "text",
      "8": "emoji",
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (mod && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        redo();
      } else if (mod && e.key === "s") {
        e.preventDefault();
        if (hasUnsavedChangesRef.current) saveRef.current();
      } else if (e.key === "Escape") {
        setSelectedStrokeId(null);
      } else if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedStrokeIdRef.current
      ) {
        e.preventDefault();
        removeStroke(selectedStrokeIdRef.current);
        setSelectedStrokeId(null);
      } else if (TOOL_KEYS[e.key]) {
        setToolSettings((prev) => ({ ...prev, tool: TOOL_KEYS[e.key] }));
        if (TOOL_KEYS[e.key] !== "select") setSelectedStrokeId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo, removeStroke]);

  const handleToolChange = useCallback((tool: DrawingTool) => {
    setToolSettings((prev) => ({ ...prev, tool }));
    if (tool !== "select") setSelectedStrokeId(null);
  }, []);

  const handleColorChange = useCallback(
    (color: string) => {
      setToolSettings((prev) => ({ ...prev, color }));
      if (selectedStrokeId) {
        updateStroke(selectedStrokeId, { color });
      }
    },
    [selectedStrokeId, updateStroke],
  );

  const handleWidthChange = useCallback(
    (width: number) => {
      setToolSettings((prev) => ({ ...prev, width }));
      if (selectedStrokeId) {
        updateStroke(selectedStrokeId, { width });
      }
    },
    [selectedStrokeId, updateStroke],
  );

  const handleEmojiSelect = useCallback((emoji: string) => {
    setToolSettings((prev) => ({ ...prev, emoji }));
  }, []);

  return (
    <div className={styles.editor}>
      <header className={styles.topBar}>
        <Group
          gap="sm"
          wrap="nowrap"
          align="flex-start"
          className={styles.topBarLeft}
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            radius="xl"
            aria-label="Close chart editor"
            onClick={handleBackRequest}
          >
            <IconX size={20} />
          </ActionIcon>
          <Stack gap={2} className={styles.titleBlock}>
            {onSave ? (
              titleEditing ? (
                <TextInput
                  ref={titleInputRef}
                  value={chartTitle}
                  onChange={(e) => setChartTitle(e.currentTarget.value)}
                  onBlur={() => commitTitleEdit()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitTitleEdit();
                    }
                  }}
                  size="sm"
                  fw={600}
                  variant="unstyled"
                  placeholder="Chart title"
                  maxLength={CHART_DISPLAY_NAME_MAX_CHARS}
                  styles={{
                    input: {
                      fontWeight: 700,
                      fontSize: "var(--mantine-font-size-lg)",
                      lineHeight: 1.25,
                    },
                  }}
                />
              ) : (
                <Group gap={6} wrap="nowrap" align="center">
                  <Text
                    fw={700}
                    size="lg"
                    className={styles.diagramTitle}
                    lineClamp={2}
                    style={{ minWidth: 0 }}
                  >
                    {chartTitle.trim() || diagramName}
                  </Text>
                  <ActionIcon
                    type="button"
                    variant="subtle"
                    color="gray"
                    size="sm"
                    radius="xl"
                    aria-label="Edit chart title"
                    onClick={() => setTitleEditing(true)}
                  >
                    <IconPencil size={18} />
                  </ActionIcon>
                </Group>
              )
            ) : (
              <Text fw={700} size="lg" className={styles.diagramTitle}>
                {diagramName}
              </Text>
            )}
            {clientDisplayName ? (
              <Text size="sm" c="dimmed">
                Client: {clientDisplayName}
              </Text>
            ) : null}
          </Stack>
        </Group>
        <Group gap="sm" wrap="nowrap" align="center">
          <Group gap={6} wrap="nowrap" align="center">
            <Text size="xs" c="dimmed" display={{ base: "none", sm: "block" }}>
              {document.strokes.length} annotation
              {document.strokes.length !== 1 ? "s" : ""}
            </Text>
            {onSave ? (
              <Badge
                size="sm"
                variant="light"
                color={hasUnsavedChanges ? "orange" : "teal"}
              >
                {hasUnsavedChanges ? "Edited" : "Saved"}
              </Badge>
            ) : null}
          </Group>
          {onSave ? (
            <Button
              variant="filled"
              color="teal"
              className={primaryAction.primaryFilled}
              size="sm"
              leftSection={<IconDeviceFloppy size={18} />}
              loading={isSaving}
              disabled={!hasUnsavedChanges}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          ) : null}
        </Group>
      </header>

      <div
        className={onLoadVersions ? styles.mainWithSidebar : styles.mainSingle}
      >
        <Box className={styles.canvasColumn}>
          <div className={styles.toolbarWrap}>
            <ChartToolbar
              className={styles.toolbarSheet}
              toolSettings={toolSettings}
              onToolChange={handleToolChange}
              onColorChange={handleColorChange}
              onWidthChange={handleWidthChange}
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
              onRevertSession={handleRevertSessionRequest}
              canRevertSession={hasSessionStrokeEdits}
              onDeleteAllAnnotations={handleDeleteAllRequest}
              canDeleteAllAnnotations={document.strokes.length > 0}
              onDownload={handleDownload}
              activeEmoji={toolSettings.emoji}
              onEmojiSelect={handleEmojiSelect}
            />
          </div>
          <div className={styles.canvasBody}>
            <ChartCanvas
              key={imageLoadKey}
              ref={canvasRef}
              baseImageUrl={canvasBaseImageUrl}
              strokes={document.strokes}
              currentStroke={currentStroke}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              toolCursor={TOOL_CURSORS[toolSettings.tool]}
              textInput={textInput}
              onTextSubmit={handleTextSubmit}
              onTextCancel={handleTextCancel}
              onRetryImage={handleRetryImage}
              selectedStrokeId={selectedStrokeId}
              onStrokeTransform={handleStrokeTransform}
            />
          </div>
        </Box>

        {onLoadVersions ? (
          <aside className={styles.versionSidebar}>
            <div className={styles.versionHeader}>
              <Text fw={700} size="md" className={styles.versionHeaderTitle}>
                Version History
              </Text>
              <Text
                size="xs"
                c="dimmed"
                className={styles.versionHeaderSubtitle}
              >
                Track changes and previous markups.
              </Text>
              <Divider className={styles.versionHeaderRule} />
            </div>
            <ScrollArea className={styles.versionScroll} type="auto">
              {loadingVersions && sortedVersions.length === 0 ? (
                // First-time load only — once we have ANY rows (real or
                // the optimistic "Saving…" placeholder pushed by
                // handleSave), keep them visible during refreshes so the
                // sidebar doesn't blank out and then repopulate on every
                // save (the "version history disappears + reappears"
                // bug Joseph diagnosed by eye). The placeholder row is
                // its own loading indicator for save-driven refreshes.
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  Loading versions...
                </Text>
              ) : sortedVersions.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  No previous versions found.
                </Text>
              ) : (
                <div className={styles.versionTimeline}>
                  <div className={styles.versionTimelineLine} aria-hidden />
                  {sortedVersions.map((v) => {
                    const isPlaceholder = v.versionUuid.startsWith(
                      "__saving_placeholder_",
                    );
                    const isCurrent =
                      !isPlaceholder && v.versionUuid === currentVersionUuid;
                    const meta = v.metadata as
                      | Record<string, unknown>
                      | null
                      | undefined;
                    const note =
                      meta && typeof meta.note === "string" && meta.note.trim()
                        ? meta.note.trim()
                        : null;
                    const label =
                      (v.displayName && v.displayName.trim()) ||
                      `Version ${v.versionNumber}`;
                    const authorName = salonDocumentVersionStaffDisplayName(v);
                    return (
                      <div
                        key={v.versionUuid}
                        className={styles.versionTimelineRow}
                      >
                        <div className={styles.versionTimelineRail}>
                          <span
                            className={
                              isCurrent
                                ? styles.versionDotCurrent
                                : styles.versionDot
                            }
                            aria-hidden
                          />
                        </div>
                        <Paper
                          withBorder
                          radius="md"
                          p="md"
                          className={
                            isCurrent
                              ? `${styles.versionCardPaper} ${styles.versionCardPaperCurrent}`
                              : styles.versionCardPaper
                          }
                        >
                          {isCurrent ? (
                            <Badge
                              size="xs"
                              variant="filled"
                              color="teal"
                              className={styles.versionCurrentBadge}
                            >
                              Current
                            </Badge>
                          ) : null}
                          <Text
                            fw={700}
                            size="sm"
                            className={styles.versionCardTitle}
                            pr={isCurrent ? 72 : 0}
                          >
                            {label}
                          </Text>
                          <Text size="xs" c="dimmed" mt={4}>
                            {formatSalonDocumentVersionTimestamp(v.createdDt)}
                          </Text>
                          {note ? (
                            <Text size="sm" mt="xs" lineClamp={4}>
                              {note}
                            </Text>
                          ) : null}
                          <div className={styles.versionCardFooter}>
                            {isPlaceholder ? (
                              <>
                                <Loader size={20} color="teal" />
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  Uploading new version…
                                </Text>
                              </>
                            ) : (
                              <>
                                <Avatar
                                  size={28}
                                  radius="xl"
                                  color="gray"
                                  className={styles.versionAvatar}
                                >
                                  {initialsFromStaffDisplayName(
                                    authorName || "Unknown",
                                  )}
                                </Avatar>
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {authorName || "—"}
                                </Text>
                              </>
                            )}
                          </div>
                          {!isCurrent && !isPlaceholder ? (
                            <Button
                              fullWidth
                              mt="sm"
                              size="xs"
                              variant="filled"
                              color="teal"
                              className={primaryAction.primaryFilled}
                              loading={restoringVersion === v.versionUuid}
                              onClick={() => handleRestoreVersion(v)}
                            >
                              Restore
                            </Button>
                          ) : null}
                        </Paper>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </aside>
        ) : null}
      </div>

      <Modal
        opened={showRevertSessionConfirm}
        onClose={() => setShowRevertSessionConfirm(false)}
        title="Reset version's changes"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            This restores annotations to how they were when you opened this
            chart (or when you last saved). Edits you made in this session will
            be discarded. Undo history for these edits will also be cleared.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setShowRevertSessionConfirm(false)}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleRevertSessionConfirm}>
              Reset changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={showDeleteAllConfirm}
        onClose={() => setShowDeleteAllConfirm(false)}
        title="Delete all annotations"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            This removes every annotation from the chart and clears the saved
            baseline to an empty canvas. You cannot bring prior annotations back
            with Undo from the toolbar; save only if you intend to keep this
            empty state.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setShowDeleteAllConfirm(false)}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteAllConfirm}>
              Delete all
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        title="Unsaved changes"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            You have unsaved changes. Are you sure you want to go back? Your
            changes will be lost.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setShowBackConfirm(false)}
            >
              Stay
            </Button>
            <Button
              color="red"
              onClick={() => {
                setShowBackConfirm(false);
                onBack();
              }}
            >
              Discard changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
